import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { Button } from "../../../components/button";
import { Card, CardContent } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../../components/editor";
import { TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Section } from "../../../components/layout";
import { ExternalLink, Link } from "../../../components/link";
import { BlockCodeWrapper, DashboardTextHeading, MutedText, Paragraph } from "../../../components/text";
import { TimeSpanPicker } from "../../../components/time-span-picker";
import { displayFormControl } from "../../../ui/alert";
import { formatDuration } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";
import { valueFrom } from "../../../util";


export default function (ctx) {

    const chatClientId = ctx.params.id;
    const chatClient = el.state.create(null);

    function updateView() {
        runOperation("Fetching chat client...", async () => {
            const response = await api.getJson(`/api/v1/web-chat-client/${chatClientId}`);
            chatClient.value = response.data;
        });
    }

    updateView();

    function handleSave() {
        const data = {
            integrationType: "EvolutionApi",
            integrations: {
                evolutionIntegration: {
                    endpointUrl: valueFrom("evoInstanceEndpoint"),
                    instanceName: valueFrom("evoInstanceName"),
                    apiKey: valueFrom("evoInstanceToken"),
                    sessionDuration: valueFrom("evoSessionDuration")
                }
            }
        };

        runOperation("Saving Evolution API integration details...", async () => {
            await api.putJson(`/api/v1/web-chat-client/${chatClientId}/integrations`, data);
        });
    }

    return el.state.listening([chatClient], () =>
        Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                    onClick() {
                        history.go(-1);
                        page();
                    }
                })
            ),
            DashboardTitle(
                DashboardTextHeading("Evolution API settings", chatClient.value?.name)
            ),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        "Save changes",
                        {
                            onClick: handleSave
                        }
                    )
                )
            ),
            Section(
                Card(
                    CardContent(
                        Paragraph(
                            "To configure your integration with your Evolution API instance, you will need to have an active WhatsApp instance connected to your server."
                        ),
                        Paragraph(
                            "After configuring your instance, set the URL below as the webhook URL for your instance:"
                        ),
                        BlockCodeWrapper("Integration webhook",
                            window.env.apiEndpoint + "api/v1/intl/evolution-api/" + chatClientId + "/hook", "url"
                        ),
                        Paragraph(
                            "Attention: for correct functioning, keep the fields as:"
                        ),
                        el("ul",
                            el("li", "Webhook by Events: ", el("strong", "Disabled")),
                            el("li", "Webhook Base64: ", el("strong", "Disabled")),
                            el("li", "Events/MESSAGES_UPSERT: ", el("strong", "Enabled")),
                        ),
                        Paragraph(
                            "Note: AIVAX is not affiliated in any way with Evolution."
                        )
                    )
                )
            ),
            Section(
                Editor(
                    {
                        id: "evoInstanceEndpoint",
                        value: chatClient.value?.integrations.evolutionIntegration?.endpointUrl,
                        renderValue(value) {
                            return Button(value || MutedText("Empty"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "https://my-instance-url.com", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The instance endpoint must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Instance Endpoint"),
                        Paragraph("Specify the base address where your Evolution API instance is accessible.")
                    )
                ),
                Editor(
                    {
                        id: "evoInstanceName",
                        value: chatClient.value?.integrations.evolutionIntegration?.instanceName,
                        renderValue(value) {
                            return Button(value || MutedText("Empty"));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "My Instance", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The instance name must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Instance Name"),
                        Paragraph("Specify the name of the instance where WhatsApp is connected.")
                    )
                ),
                Editor(
                    {
                        id: "evoInstanceToken",
                        value: chatClient.value?.integrations.evolutionIntegration?.apiKey,
                        renderValue(value) {
                            return Button("👀");
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TextInput(null, "70803BD...3D22A", { value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x.length > 3, "The instance token must be at least 3 characters long."]
                                ],
                                nullButton: true
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Instance token"),
                        Paragraph("Specify the secret token of the instance used to send messages.")
                    )
                ),
            ),
            Section(
                Editor(
                    {
                        id: "evoSessionDuration",
                        value: chatClient.value?.integrations.evolutionIntegration?.sessionDuration || 10800,
                        renderValue(value) {
                            return Button(formatDuration(value));
                        },
                        manipulateValue(value, callback) {
                            displayFormControl({
                                title: this.editorTitle,
                                message: this.editorParagraphs,
                                formControlItem: TimeSpanPicker({ value: value }),
                                onConfirm: callback,
                                validate: [
                                    [x => x >= 600, "The duration must be greater than or equal to 10 minutes."],
                                    [x => x <= 7776000, "The duration must be less than or equal to 90 days."],
                                ]
                            })
                        }
                    },
                    EditorDescription(
                        EditorTitle("Session duration"),
                        Paragraph("Specify the duration that a conversation has before being restarted.")
                    )
                ),
            )
        )
    )
}